/* HelpVideoModal — "How to use the platform" video guide.
 * Opened from the header help button, and auto-shown once on a teacher's
 * first login (tracked on the account via help_video_seen).
 * The URL is admin-configurable (Admin panel → Help video); falls back to
 * the default Bunny.net embed. */
import React, { useEffect, useState } from "react";
import { X, PlayCircle } from "lucide-react";
import api from "@/lib/api";

const DEFAULT_EMBED_URL = "https://iframe.mediadelivery.net/embed/683427/a6d10dd6-4d8e-451c-9e13-ac0c45abf7c2?autoplay=true&preload=true";

// Accepts plain YouTube links too and converts them to embeddable URLs.
const toEmbed = (u) => {
  try {
    const url = new URL(u);
    if (url.hostname.includes("youtube.com") && url.searchParams.get("v")) return `https://www.youtube.com/embed/${url.searchParams.get("v")}?autoplay=1`;
    if (url.hostname === "youtu.be" && url.pathname.length > 1) return `https://www.youtube.com/embed${url.pathname}?autoplay=1`;
  } catch { /* not a URL — use as-is */ }
  return u;
};

export function HelpVideoModal({ open, onClose, firstTime = false }) {
  const [videoUrl, setVideoUrl] = useState(DEFAULT_EMBED_URL);
  useEffect(() => {
    if (!open) return;
    api.get("/public/settings/help-video")
      .then(({ data }) => { if (data?.url) setVideoUrl(toEmbed(data.url)); })
      .catch(() => { /* fall back to default */ });
  }, [open]);
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[120] bg-black/75 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="help-video-modal"
    >
      <div
        className="w-full max-w-4xl bg-[#0D1117] border border-[#22D3EE]/30 rounded-2xl shadow-[0_0_60px_-10px_rgba(34,211,238,0.45)] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#7C3AED] to-[#22D3EE] flex items-center justify-center">
              <PlayCircle size={18} className="text-white" />
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#22D3EE]">
                {firstTime ? "Welcome aboard!" : "Video guide"}
              </div>
              <h3 className="font-display font-bold text-lg tracking-tight text-white leading-tight">
                How to use TeachingSoftware.ai
              </h3>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="help-video-close"
            className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/15 text-white/70 hover:text-white inline-flex items-center justify-center transition-colors"
          ><X size={16} /></button>
        </div>
        <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
          <iframe
            src={videoUrl}
            title="How to use TeachingSoftware.ai"
            loading="lazy"
            className="absolute inset-0 w-full h-full border-0"
            allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
            data-testid="help-video-iframe"
          />
        </div>
        <div className="px-6 py-3 flex items-center justify-between bg-white/[0.03]">
          <span className="text-[11px] text-white/40 font-mono">You can rewatch this anytime from the <strong className="text-[#22D3EE]">Video Help</strong> button in the header.</span>
          <button
            onClick={onClose}
            data-testid="help-video-done"
            className="px-4 py-1.5 rounded-full bg-[#22D3EE] text-[#0A0C10] text-xs font-bold hover:bg-[#22D3EE]/85 transition-colors"
          >Got it</button>
        </div>
      </div>
    </div>
  );
}
